import * as React from "react";
import { useState } from "react";
import GooglePlaceAutocompleteInput, { Usage } from "./GooglePlaceAutocompleteInput";
import SelectInput from "./SelectInput";
import TextInput from "./TextInput";
import Address from "../../../model/address/Address";
import { required } from "../../../model/form/Validators";
import "./AddressField.scss";
import Country from "../../../model/address/Country";
import Option from "../../../model/Option";

interface AddressFieldProps {
  countries: Country[];
  googleApiKey: string;
  onCountryChange?: (country: Country) => any;
  initialValue?: Address;
}

export default function AddressField(props: AddressFieldProps) {
  const googleApiKeyDisabled = document.location.host === "localhost:8080";
  const [googlePlaceEnabled, setGooglePlaceEnabled] = useState<boolean>(!googleApiKeyDisabled);

  const switchMode = () => setGooglePlaceEnabled((state) => !state);

  return (
    <>
      {googlePlaceEnabled ? (
        <GoogleAddressTemplate
          countries={props.countries}
          googleApiKey={props.googleApiKey}
          initialValue={props.initialValue}
          onCountryChange={props.onCountryChange}
          switchMode={switchMode}
        />
      ) : (
        <CustomAddressTemplate
          countries={props.countries}
          initialValue={props.initialValue}
          onCountryChange={props.onCountryChange}
          switchMode={!googleApiKeyDisabled && switchMode}
        />
      )}
    </>
  );
}

function GoogleAddressTemplate({
  countries,
  initialValue,
  googleApiKey,
  switchMode,
  onCountryChange,
}) {
  return (
    <>
      <GooglePlaceAutocompleteInput
        countries={countries}
        name="place"
        label={i18n["Global.Address"]}
        selectedLabel={i18n["Global.Address.Selected"]}
        googleApiKey={googleApiKey}
        usage={Usage.ADDRESS}
        initialValue={initialValue}
        onChange={
          onCountryChange &&
          ((input) => {
            const address: Address = input.value;
            onCountryChange(address.country);
          })
        }
      />
      <div className="h-flex">
        <div className="address-switch-mode" onClick={switchMode}>
          {i18n["Global.Address.Switch.ToCustom"]}
        </div>
      </div>
      <TextInput
        name="additionalInfo"
        label={i18n["Global.AddressAdditionalInfo"]}
        placeholder={i18n["Form.DoctorRegistration.Placeholder.PracticeName"]}
        detail={i18n["Global.OptionalField"]}
        initialValue={initialValue?.additionalInfo}
      />
    </>
  );
}

function CustomAddressTemplate({
  countries,
  initialValue,
  onCountryChange,
  switchMode,
}: {
  countries: Country[];
  initialValue?: Address;
  onAddressChange?: (address: Address) => any;
  onCountryChange?: (country: Country) => any;
  switchMode?: () => any;
}) {
  const sortedCountries = countries
    .map((country) => new Option(country.name, country.i18n))
    .sort((a, b) => a.labelAsString.localeCompare(b.labelAsString));

  return (
    <>
      <TextInput
        name="number"
        label={i18n["Global.Number"]}
        placeholder={i18n["Form.DoctorRegistration.Placeholder.Number"]}
        detail={i18n["Global.OptionalField"]}
        initialValue={initialValue?.number}
      />
      <TextInput
        name="route"
        label={i18n["Global.Address"]}
        placeholder={i18n["Form.DoctorRegistration.Placeholder.Address"]}
        validators={[required()]}
        initialValue={initialValue?.route}
      />
      {switchMode && (
        <div className="h-flex">
          <div className="address-switch-mode" onClick={switchMode}>
            {i18n["Global.Address.Switch.ToGoogle"]}
          </div>
        </div>
      )}
      <div className="message warning custom-address-warning">
        {i18n["Form.DoctorRegistration.CustomAddress.Warning"]}
      </div>
      <TextInput
        name="additionalInfo"
        label={i18n["Global.AddressAdditionalInfo"]}
        placeholder={i18n["Form.DoctorRegistration.Placeholder.PracticeName"]}
        detail={i18n["Global.OptionalField"]}
        initialValue={initialValue?.additionalInfo}
      />
      <TextInput
        name="city"
        label={i18n["Global.City"]}
        placeholder={i18n["Form.DoctorRegistration.Placeholder.City"]}
        validators={[required()]}
        initialValue={initialValue?.city}
      />
      <TextInput
        name="zipCode"
        label={i18n["Global.ZipCode"]}
        placeholder={i18n["Form.DoctorRegistration.Placeholder.ZipCode"]}
        detail={i18n["Global.OptionalField"]}
        initialValue={initialValue?.zipCode}
      />
      <TextInput
        name="state"
        label={i18n["Global.State"]}
        placeholder={i18n["Form.DoctorRegistration.Placeholder.State"]}
        detail={i18n["Global.OptionalField"]}
        initialValue={initialValue?.state}
      />
      <SelectInput
        name="country"
        label={i18n["Global.Country"]}
        placeholder={i18n["Form.DoctorRegistration.Placeholder.Country"]}
        options={sortedCountries}
        onChange={
          onCountryChange &&
          ((input) => {
            let country = countries.find((country) => country.name === input.value);
            if (!country) {
              country = countries[0];
            }
            onCountryChange(country);
          })
        }
        validators={[required()]}
        initialValue={initialValue?.country?.name}
      />
    </>
  );
}
